import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { keyBy } from 'lodash';
import { exportAsCsv } from '../Exporter/service';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../i18next';
import { now, toMoment } from '../../services/time';
import { UserTableColumn } from './columns';
import { toExportColumns } from './toExportColumns';

export const useExportUsersCsv = ({
  users,
  selectedUsers,
  selectedColumns,
  customUserFields,
  dateFormat,
  fileName = 'Dado – Employee data export'
}: {
  users: Doc<IUser>[] | void;
  selectedUsers: Doc<IUser>[] | null;
  selectedColumns: UserTableColumn[];
  customUserFields: CustomUserField[];
  dateFormat: string;
  fileName?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { enqueueSnackbarError } = useSnackbar();

  const exportCsv = async () => {
    if (!users || !selectedUsers) {
      return;
    }
    try {
      const exportColumns = toExportColumns(selectedColumns, customUserFields);
      const otherProps = {
        dateFormat,
        additionalUsers: EMPTY_OBJ,
        usersById: keyBy(users, (u) => u.id)
      };
      // '/' is a path separator in a download filename, so dates in the org's
      // configured format (often 'MM/DD/YYYY') need their separators swapped.
      const today = toMoment(now()).format(dateFormat).replace(/\//g, '-');
      await exportAsCsv(
        `${fileName} - ${today}`,
        selectedUsers,
        otherProps,
        exportColumns
      );
    } catch (error) {
      console.error('Error exporting employee CSV:', error);
      enqueueSnackbarError(t('Something went wrong. Please try again.'));
    }
  };

  return {
    exportCsv,
    canExport: !!selectedUsers?.length && selectedColumns.length > 0
  };
};
